import {
  useLayoutEffect, useRef, type CSSProperties, type ElementType, type ReactNode, type RefObject,
} from 'react';
import { gsap } from 'gsap';
import { isHeroVisible, onHeroChange } from '@/lib/shutter/hero-layer-store';

/**
 * FINAL IN-PAGE MOTION (GSAP + ScrollTrigger).
 * FAIL-OPEN: no CSS ever hides content. GSAP applies the start state only after
 * ScrollTrigger is created successfully; any throw → context reverted → static page.
 * Entrance plays once (start "top 88%"), then inline styles are cleared.
 */
let registered = false;
function ready(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return false;
    if (typeof IntersectionObserver === 'undefined') return false;
    registered = true;
    return registered;
  } catch { return false; }
}
const isMobile = () => { try { return window.matchMedia('(max-width: 767px)').matches; } catch { return false; } };


export type RevealVariant = 'text' | 'title' | 'micro' | 'fade' | 'block' | 'row' | 'note' | 'rate' | 'info'
  | 'studio-text' | 'support' | 'footer' | 'title-strong' | 'micro-strong' | 'copy' | 'cta';

type Spec = { y?: number; x?: number; d: number; base?: number; st?: number };
const SPEC: Record<RevealVariant, Spec> = {
  micro: { y: 12, d: 0.5 },
  title: { y: 28, d: 0.7, base: 0.08 },
  fade: { x: 16, d: 0.55, base: 0.16 },
  'micro-strong': { y: 12, d: 0.5, base: 0.05 },
  'title-strong': { y: 36, d: 0.8, base: 0.15 },
  text: { y: 24, d: 0.65 },
  block: { y: 28, d: 0.75 },
  footer: { y: 28, d: 0.75 },
  support: { y: 16, d: 0.6 },
  copy: { y: 14, d: 0.55, base: 0.08 },
  cta: { y: 12, d: 0.5, base: 0.16 },
  'studio-text': { y: 36, d: 0.8 },
  row: { y: 28, d: 0.65, st: 0.08 },
  note: { y: 30, d: 0.7, st: 0.12 },
  rate: { y: 20, d: 0.55, st: 0.07 },
  info: { y: 20, d: 0.6, st: 0.08 },
};

const variantOf = (el: Element): RevealVariant => {
  const m = /mo--([a-z-]+)/.exec(el.className && typeof el.className === 'string' ? el.className : '');
  return (m && (m[1] as RevealVariant) in SPEC ? m[1] : 'block') as RevealVariant;
};

/**
 * Shared runner. Uses LIVE geometry (IntersectionObserver) instead of precomputed
 * scroll positions, and waits until the home Hero layer is closed, so entrances
 * never play hidden behind the Hero or before the user reaches them.
 */
function useEntrance<T extends HTMLElement>(ref: RefObject<T | null>, build: (tl: gsap.core.Timeline, el: T, k: number, sk: number) => void) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !ready()) return undefined;
    const mobile = isMobile();
    const k = mobile ? 0.65 : 1;
    const sk = mobile ? 0.8 : 1;
    let ctx: gsap.Context | null = null;
    let tl: gsap.core.Timeline | null = null;
    let io: IntersectionObserver | null = null;
    let unsub: (() => void) | null = null;
    let timer = 0;
    const fail = () => { try { io?.disconnect(); ctx?.revert(); } catch { /* noop */ } ctx = null; };
    const observe = (delay: number) => {
      timer = window.setTimeout(() => {
        try {
          io = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
              if (e.isIntersecting) { tl?.play(); io?.disconnect(); } else if (e.boundingClientRect.bottom <= 0) {
                tl?.progress(1); io?.disconnect(); // already scrolled past: show immediately
              }
            });
          }, { threshold: 0.12, rootMargin: '0px 0px -12% 0px' });
          io.observe(el);
        } catch { fail(); }
      }, delay);
    };
    try {
      ctx = gsap.context(() => {
        tl = gsap.timeline({ paused: true });
        build(tl, el, k, sk);
      }, el);
      if (isHeroVisible()) {
        // Everything is under the Hero layer: start watching once it closes
        // (small delay so the shutter aperture has opened).
        unsub = onHeroChange(() => {
          if (!isHeroVisible() && unsub) { unsub(); unsub = null; observe(450); }
        });
      } else {
        observe(0);
      }
    } catch { fail(); }
    return () => {
      window.clearTimeout(timer);
      unsub?.();
      try { io?.disconnect(); ctx?.revert(); } catch { /* noop */ }
    };
  }, [ref, build]);
}

const CLEAR = 'opacity,transform,visibility';

function addItem(tl: gsap.core.Timeline, target: Element, spec: Spec, at: number, k: number) {
  tl.fromTo(target, {
    autoAlpha: 0, y: (spec.y ?? 0) * k, x: (spec.x ?? 0) * k,
  }, {
    autoAlpha: 1, y: 0, x: 0, duration: spec.d, ease: 'power3.out', clearProps: CLEAR,
  }, at);
}

/** Kept for call-site compatibility; stagger is now computed in GSAP. */
export const moDelay = (_delay = 0, _index = 0, _stagger = 0) => ({}) as CSSProperties;

interface RevealProps {
  as?: ElementType;
  variant?: RevealVariant;
  index?: number;
  stagger?: number;
  delay?: number;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  [key: string]: unknown;
}

/** Single element entrance. `footer` also staggers its inner groups. */
export default function Reveal({
  as: Tag = 'div', variant = 'block', index = 0, stagger = 0, delay = 0, className = '', style, children, ...rest
}: RevealProps) {
  const ref = useRef<HTMLElement>(null);
  const buildRef = useRef((tl: gsap.core.Timeline, el: HTMLElement, k: number, sk: number) => {
    const spec = SPEC[variant];
    const at = (delay + index * stagger * sk) / 1000;
    addItem(tl, el, spec, at, k);
    if (variant === 'footer') {
      el.querySelectorAll(':scope > div > *').forEach((c, i) => {
        tl.fromTo(c, { autoAlpha: 0, y: 14 * k }, {
          autoAlpha: 1, y: 0, duration: 0.6, ease: 'power2.out', clearProps: CLEAR,
        }, at + 0.15 + i * 0.07 * sk);
      });
    }
  });
  useEntrance(ref, buildRef.current);
  return <Tag ref={ref} className={`mo mo--${variant} ${className}`} style={style} {...rest}>{children}</Tag>;
}

function staggerChildren(tl: gsap.core.Timeline, el: HTMLElement, k: number, sk: number) {
  const items = Array.from(el.querySelectorAll<HTMLElement>('.mo-item, .mo-aitem'));
  let listIdx = 0;
  items.forEach((item) => {
    const spec = SPEC[variantOf(item)];
    const at = (spec.base ?? 0) + (spec.st ? listIdx * spec.st * sk : 0);
    if (spec.st) listIdx += 1;
    addItem(tl, item, spec, at, k);
    if (item.classList.contains('mo-rule') && !isMobile()) {
      tl.fromTo(item, { '--rule-s': 0 }, { '--rule-s': 1, duration: 0.75, ease: 'power2.out', clearProps: '--rule-s' }, at);
    }
  });
}

/** Group: `.mo-item` / `.mo-aitem` children enter with variant-based stagger. */
export function RevealGroup({
  as: Tag = 'div', className = '', children, ...rest
}: { as?: ElementType; className?: string; children?: ReactNode; [key: string]: unknown }) {
  const ref = useRef<HTMLElement>(null);
  useEntrance(ref, staggerChildren);
  return <Tag ref={ref} className={`mo-group ${className}`} {...rest}>{children}</Tag>;
}

/**
 * List group or media host. `media` animates ONLY its direct <img> (container fixed,
 * overflow hidden); nested headers keep their own entrance.
 */
export function ArmedGroup({
  as: Tag = 'div', kind = 'items', mediaScale = 1.05, mediaDur = 1, className = '', children, ...rest
}: { as?: ElementType; kind?: 'items' | 'media'; mediaScale?: number; mediaDur?: number; className?: string; children?: ReactNode; [key: string]: unknown }) {
  const ref = useRef<HTMLElement>(null);
  const buildRef = useRef((tl: gsap.core.Timeline, el: HTMLElement, k: number, sk: number) => {
    if (kind === 'media') {
      const img = el.querySelector(':scope > img');
      if (!img) return;
      const s = 1 + (mediaScale - 1) * (k < 1 ? 0.5 : 1);
      tl.fromTo(img, { autoAlpha: 0, scale: s }, {
        autoAlpha: 1, scale: 1, duration: mediaDur, ease: 'power3.out', clearProps: CLEAR,
      }, 0);
      return;
    }
    staggerChildren(tl, el, k, sk);
  });
  useEntrance(ref, buildRef.current);
  const base = kind === 'media' ? 'mo-media' : 'mo-agroup';
  return <Tag ref={ref} className={`${base} ${className}`} {...rest}>{children}</Tag>;
}

/** Single element (alias of Reveal, kept for existing call sites). */
export function ArmedReveal(props: RevealProps) {
  return <Reveal {...props} />;
}

/**
 * Work outer wrapper: animates `top` (position:relative) + opacity, never transform,
 * so Work's fixed-position layers keep their containing block.
 */
export function WorkEntrance({ className = '', children }: { className?: string; children?: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const buildRef = useRef((tl: gsap.core.Timeline, el: HTMLDivElement, k: number) => {
    tl.fromTo(el, { opacity: 0, top: 28 * k }, {
      opacity: 1, top: 0, duration: 0.75, ease: 'power3.out', clearProps: 'opacity,top',
    }, 0);
  });
  useEntrance(ref, buildRef.current);
  return <div ref={ref} className={`relative ${className}`}>{children}</div>;
}